import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

export default function PinGate() {
  const { login } = useAuth();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!pin || busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(pin);
    } catch (err) {
      setPin('');
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many tries in a row. Give it a minute and try again.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('That’s not it — double-check the PIN and try again.');
      } else {
        setError('Something hiccuped. Check the connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-6">
      <div className="w-full max-w-xs text-center">
        <h1 className="font-serif text-5xl font-semibold tracking-tight">Cookbook</h1>
        <p className="mt-2 font-serif text-ink-soft">The house recipes. Enter the PIN once and you’re in for good.</p>
        <form onSubmit={submit} className="mt-8">
          <label htmlFor="pin" className="sr-only">
            PIN
          </label>
          <input
            id="pin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              setError(null);
            }}
            placeholder="· · · ·"
            className="h-14 w-full rounded-xl border border-line bg-card text-center font-sans text-2xl tracking-[0.5em] placeholder:tracking-[0.2em] placeholder:text-ink-soft/50 focus:border-ink-soft focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy || !pin}
            className="mt-3 h-12 w-full rounded-xl bg-ink font-sans text-[0.95rem] font-semibold text-paper transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            {busy ? 'Checking…' : 'Open the book'}
          </button>
          <p role="status" aria-live="polite" className="mt-4 min-h-6 font-serif text-[0.95rem] text-goal">
            {error}
          </p>
        </form>
      </div>
    </main>
  );
}
